import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Capacitor } from '@capacitor/core';
import 'react-toastify/dist/ReactToastify.css';
import logo from "./Images/logo.png";
import './App.css';
import { AuthProvider, useAuth } from './Auth/AuthContext';
import ProtectedRoute, { PublicRoute } from './ProtectedRoute';
const Home = lazy(() => import('./Pages/Home/Home'));
const Products = lazy(() => import('./Pages/Products/Products'));
const Product = lazy(() => import('./Pages/Product/Product'));
const Companies = lazy(() => import('./Pages/Companies/Companies'));
const OrderBookers = lazy(() => import('./Pages/OrderBookers/OrderBookers'));
const BookedOrders = lazy(() => import('./Pages/BookedOrders/BookedOrders'));
const Customers = lazy(() => import('./Pages/Customers/Customers'));
const Sales = lazy(() => import('./Pages/Sales/Sales'));
const SalesInvoice = lazy(() => import('./Pages/Sales/SalesInvoice'));
const BulkInvoicePrint = lazy(() => import('./Pages/Sales/BulkInvoicePrint'));
const Payments = lazy(() => import('./Pages/Payments/Payments'));
const Expenses = lazy(() => import('./Pages/Expenses/Expenses'));
const Inventory = lazy(() => import('./Pages/Inventory/Inventory'));
const Reports = lazy(() => import('./Pages/Reports/Reports'));
const ReportDetail = lazy(() => import('./Pages/Reports/ReportDetail'));
const Users = lazy(() => import('./Pages/Users/Users'));
const AuditLogs = lazy(() => import('./Pages/AuditLogs/AuditLogs'));
const AppUpdates = lazy(() => import('./Pages/AppUpdates/AppUpdates'));
const Login = lazy(() => import('./Pages/Login/Login'));
const MobileApp = lazy(() => import('./Mobile/MobileApp'));

function HomeRoute() {
  const { user, token, logout } = useAuth();
  return <Home user={user} token={token} onLogout={logout} />;
}

function ProductsRoute() {
  const { user, logout } = useAuth();
  return <Products user={user} onLogout={logout} />;
}

function ProductRoute() {
  const { user, logout } = useAuth();
  return <Product user={user} onLogout={logout} />;
}

function CompaniesRoute() {
  const { user, logout } = useAuth();
  return <Companies user={user} onLogout={logout} />;
}

function OrderBookersRoute() {
  const { user, logout } = useAuth();
  return <OrderBookers user={user} onLogout={logout} />;
}

function CustomersRoute() {
  const { user, logout } = useAuth();
  return <Customers user={user} onLogout={logout} />;
}

function SalesRoute({ createPage = false }) {
  const { user, logout } = useAuth();
  return <Sales user={user} onLogout={logout} createPage={createPage} />;
}

function SalesInvoiceRoute() {
  const { user, logout } = useAuth();
  return <SalesInvoice user={user} onLogout={logout} />;
}

function BulkInvoicePrintRoute() {
  const { user, logout } = useAuth();
  return <BulkInvoicePrint user={user} onLogout={logout} />;
}

function PaymentsRoute() {
  const { user, logout } = useAuth();
  return <Payments user={user} onLogout={logout} />;
}

function ExpensesRoute() {
  const { user, logout } = useAuth();
  if (String(user?.role || '').toLowerCase() !== 'owner') return <Navigate to="/" replace />;
  return <Expenses user={user} onLogout={logout} />;
}

function InventoryRoute() {
  const { user, logout } = useAuth();
  return <Inventory user={user} onLogout={logout} />;
}

function ReportsRoute() {
  const { user, logout } = useAuth();
  return <Reports user={user} onLogout={logout} />;
}

function UsersRoute() {
  const { user, logout } = useAuth();
  if (String(user?.role || '').toLowerCase() !== 'owner') return <Navigate to="/" replace />;
  return <Users user={user} onLogout={logout} />;
}

function AuditLogsRoute() {
  const { user, logout } = useAuth();
  if (String(user?.role || '').toLowerCase() !== 'owner') return <Navigate to="/" replace />;
  return <AuditLogs user={user} onLogout={logout} />;
}

function AppUpdatesRoute() {
  const { user, logout } = useAuth();
  if (!['owner', 'manager'].includes(String(user?.role || '').toLowerCase())) return <Navigate to="/" replace />;
  return <AppUpdates user={user} onLogout={logout} />;
}

function ReportDetailRoute() {
  const { user, logout } = useAuth();
  const { reportType } = useParams();
  if (!['order-bookers', 'order-booker-detail', 'sales', 'profit', 'profit-by-invoice', 'stock', 'customer-credit', 'order-booker-credit'].includes(reportType)) return <Navigate to="/reports" replace />;
  return <ReportDetail user={user} onLogout={logout} reportType={reportType} />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<div className="app-loading"><div className="app-loading-brand"><img src={logo} alt="Abdullah Traders" className="app-loading-logo" /><span className="loading-spinner" aria-hidden="true" /></div><span>Loading workspace…</span></div>}>
    <Routes>
      <Route path="/" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><ProductsRoute /></ProtectedRoute>} />
      <Route path="/products/new" element={<ProtectedRoute><ProductRoute /></ProtectedRoute>} />
      <Route path="/products/:id/edit" element={<ProtectedRoute><ProductRoute /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><CompaniesRoute /></ProtectedRoute>} />
      <Route path="/order-bookers" element={<ProtectedRoute><OrderBookersRoute /></ProtectedRoute>} />
      <Route path="/booked-orders" element={<ProtectedRoute><BookedOrders /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomersRoute /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><SalesRoute /></ProtectedRoute>} />
      <Route path="/sales/new" element={<ProtectedRoute><SalesRoute createPage /></ProtectedRoute>} />
      <Route path="/sales/print" element={<ProtectedRoute><BulkInvoicePrintRoute /></ProtectedRoute>} />
      <Route path="/sales/:id/edit" element={<ProtectedRoute><SalesRoute /></ProtectedRoute>} />
      <Route path="/sales/:id" element={<ProtectedRoute><SalesInvoiceRoute /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><PaymentsRoute /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesRoute /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><InventoryRoute /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsRoute /></ProtectedRoute>} />
      <Route path="/reports/:reportType" element={<ProtectedRoute><ReportDetailRoute /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersRoute /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogsRoute /></ProtectedRoute>} />
      <Route path="/app-updates" element={<ProtectedRoute><AppUpdatesRoute /></ProtectedRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastContainer position="top-right" autoClose={4000} />
        {Capacitor.isNativePlatform() ? <Suspense fallback={<div className="app-loading">Opening order booker app…</div>}><MobileApp /></Suspense> : <AppRoutes />}
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
